import {
  Box,
  CircularProgress,
  CssBaseline,
  Fade,
  Stack,
  ThemeProvider,
  Typography,
  useMediaQuery,
} from "@mui/material";
import * as Sentry from "@sentry/react";
import { isNil } from "lodash-es";
import { useEffect, useMemo, useRef, useState } from "react";

import BrowserNotSupportedChecker from "@/common/components/BrowserNotSupportedChecker/BrowserNotSupportedChecker";
import { THEMES } from "@/common/constants/common";
import { LocalStorageKey } from "@/common/constants/localStorage";
import { ROUTE_PATH } from "@/common/constants/routing";
import { LocalStorageHelper } from "@/common/helpers/storage/localStorage";
import { useAuthorizationInfo } from "@/common/hooks/auth/useAuthorizationInfo";
import { useAppDispatch, useAppSelector } from "@/common/hooks/redux";
import { useLatestRef } from "@/common/hooks/ref/useLatestRef";
import { useRefWithFactory } from "@/common/hooks/ref/useRefWithFactory";
import { useAppHistory } from "@/common/hooks/routing/useAppHistory";
import { useInAppNavigationCountTracking } from "@/common/hooks/routing/useInAppNavigationCountTracking";
import {
  InternetConnectionStatus,
  useInternetConnectionStatus,
} from "@/common/hooks/useInternetConnectionStatus";
import { Stopwatch } from "@/common/stopwatch";
import { sentryConfig } from "@/config/config";
import { useFirebaseMessaging } from "@/firebase";
import * as appCommonSlice from "@/store/appCommon/slice";

import { createCustomTheme } from "../theme";
import Content from "./Content/Content";

const appErrorTimeoutMs = 60_000;
const stillLoadingTimeoutMs = 10_000;

const themeInLocalStorage = LocalStorageHelper.getItem(LocalStorageKey.theme);

export interface AppProps {
  onLoaded?: (loadingStopwatch: Stopwatch) => void;
}

/**
 * !!!!!!!!!!!!
 * NB: Redirects.
 * Do not do redirects here (except redirects to general error pages).
 * By redirecting from here you can easily break other pages without knowing.
 *
 * NB: Loading status.
 * Don't wait for API status here as it significantly delays app rendering.
 */
function App({ onLoaded }: AppProps) {
  const dispatch = useAppDispatch();
  const history = useAppHistory();

  useInAppNavigationCountTracking();
  const internetStatus = useInternetConnectionStatus();
  // const apiStatus = useApiConnectionStatus({ withRecurringCheck: true });
  const authorizationInfo = useAuthorizationInfo();

  const settings = useAppSelector((x) => x.app.settings);
  const isAppInitialized = useAppSelector((x) => x.app.isAppInitialized);
  const isAuthenticated = useAppSelector((x) => x.auth.isAuthenticated);

  const isAuthenticationInited = !isNil(isAuthenticated);
  const isAuthorizationInited = authorizationInfo.isInited; // here we expect for authorization profile to be inited by auth module

  useFirebaseMessaging(isAuthenticated);

  const [isAppLoading, setIsAppLoading] = useState(true);
  const [isStillLoadingVisible, setIsStillLoadingVisible] = useState(false);

  const onLoadedRef = useLatestRef(onLoaded);
  const appErrorTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const totalSwRef = useRefWithFactory<Stopwatch>(() => Stopwatch.startNew());
  const appInitSwRef = useRefWithFactory<Stopwatch>(() => Stopwatch.startNew());
  const authenticationSwRef = useRefWithFactory<Stopwatch>(() => Stopwatch.startNew());
  const authorizationSwRef = useRefWithFactory<Stopwatch>(() => Stopwatch.startNew());
  const internetStatusSwRef = useRefWithFactory<Stopwatch>(() => Stopwatch.startNew());
  // const apiStatusSwRef = useRefWithFactory<Stopwatch>(() => Stopwatch.startNew());

  // console.log(
  //   "App.",
  //   moment(performance.now()).format("HH:mm:ss.SSS"),
  //   {
  //     isAppLoading,
  //     isAppInitialized,
  //     isAuthenticationInited,
  //     isAuthorizationInited,
  //     isInternetConnected: internetStatus.isConnected,
  //     // isApiConnected: apiStatus.isConnected,
  //   },
  //   {
  //     isAuthenticated,
  //     isPermissionsReady: authorizationInfo.isPermissionsReady,
  //     internetStatus,
  //     // apiStatus,
  //   },
  // );

  // set initial theme based on system preference
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
  useEffect(() => {
    if (!themeInLocalStorage) {
      dispatch(appCommonSlice._themeChange(prefersDarkMode ? THEMES.DARK : THEMES.LIGHT));
    }
  }, [prefersDarkMode, dispatch]);

  const theme = useMemo(
    () =>
      createCustomTheme({
        theme: settings.theme,
        responsiveFontSizes: false,
      }),
    [settings.theme],
  );

  useEffect(() => {
    if (!isAppInitialized) {
      dispatch(appCommonSlice.appInitialized());
    }
  }, []);

  useEffect(() => {
    if (isAppLoading) {
      setIsAppLoading(
        !isAppInitialized ||
          !isAuthenticationInited ||
          !isAuthorizationInited ||
          !internetStatus.isConnected,
        // !apiStatus.isConnected,
      );
    }
  }, [
    isAppLoading,
    isAppInitialized,
    isAuthenticationInited,
    isAuthorizationInited,
    internetStatus.isConnected,
    // apiStatus.isConnected,
  ]);

  // notify user that app is still loading
  useEffect(() => {
    setTimeout(() => {
      setIsStillLoadingVisible(true);
    }, stillLoadingTimeoutMs);
  }, []);

  // show error page if app is still loading (e.g. no internet).
  useEffect(() => {
    clearTimeout(appErrorTimeoutRef.current);
    appErrorTimeoutRef.current = undefined;

    if (isAppLoading) {
      appErrorTimeoutRef.current = setTimeout(() => {
        // console.error(`App load error after ${appErrorTimeoutMs} ms.`, {
        //   internetStatus: internetStatus.getStatus(),
        //   apiStatus: apiStatus.getStatus(),
        // });
        setIsAppLoading(false);
        if (internetStatus.getStatus() === InternetConnectionStatus.Disconnected) {
          history.push(ROUTE_PATH.ERROR_NO_INTERNET({ returnPath: history.location.pathname }));
          return;
        }
        // if (apiStatus.getStatus() === ApiConnectionStatus.Disconnected) {
        //   history.push(ROUTE_PATH.ERROR_NO_API_CONNECTION({ returnPath: history.location.pathname }));
        //   return;
        // }
      }, appErrorTimeoutMs);
    }
  }, []);

  // measure app init time
  if (!isAppLoading && totalSwRef.current.isRunning) {
    totalSwRef.current.stop();
  }
  if (isAppInitialized && appInitSwRef.current.isRunning) {
    appInitSwRef.current.stop();
  }
  if (isAuthenticationInited && authenticationSwRef.current.isRunning) {
    authenticationSwRef.current.stop();
  }
  if (isAuthorizationInited && authorizationSwRef.current.isRunning) {
    authorizationSwRef.current.stop();
  }
  if (internetStatus.isConnected && internetStatusSwRef.current.isRunning) {
    internetStatusSwRef.current.stop();
  }
  // if (apiStatus.isConnected && apiStatusSwRef.current.isRunning) {
  //   apiStatusSwRef.current.stop();
  // }
  useEffect(() => {
    if (!isAppLoading) {
      onLoadedRef.current && onLoadedRef.current(totalSwRef.current);

      console.info(
        `Startup. App loaded.`,
        {
          ms: totalSwRef.current.elapsedMilliseconds,
          sec: totalSwRef.current.elapsedSeconds,
        },
        {
          total: {
            ms: totalSwRef.current.elapsedMilliseconds,
            sec: totalSwRef.current.elapsedSeconds,
          },
          appInit: {
            ms: appInitSwRef.current.elapsedMilliseconds,
            sec: appInitSwRef.current.elapsedSeconds,
          },
          authentication: {
            ms: authenticationSwRef.current.elapsedMilliseconds,
            sec: authenticationSwRef.current.elapsedSeconds,
          },
          authorization: {
            ms: authorizationSwRef.current.elapsedMilliseconds,
            sec: authorizationSwRef.current.elapsedSeconds,
          },
          internetStatus: {
            ms: internetStatusSwRef.current.elapsedMilliseconds,
            sec: internetStatusSwRef.current.elapsedSeconds,
          },
          // apiStatus: {
          //   ms: apiStatusSwRef.current.elapsedMilliseconds,
          //   sec: apiStatusSwRef.current.elapsedSeconds,
          // },
        },
      );
    }
  }, [isAppLoading]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserNotSupportedChecker />

      <Box
        data-testid='App'
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
          height: "100%",
          minHeight: "100%",
          maxHeight: "100%",
        }}
      >
        {/* Fullscreen loader */}
        {isAppLoading && (
          <Stack
            direction='column'
            spacing={2}
            alignItems='center'
            justifyContent='center'
            sx={{
              width: "100vw",
              height: "100vh",
            }}
          >
            <CircularProgress size='50px' color='primary' />

            {isStillLoadingVisible && (
              <Fade in={isStillLoadingVisible}>
                <Typography component='div' variant='body2'>
                  {`Hang tight - we're getting everything ready for you.`}
                </Typography>
              </Fade>
            )}
          </Stack>
        )}

        {/* Content */}
        {!isAppLoading && <Content />}
      </Box>
    </ThemeProvider>
  );
}
export default sentryConfig.isEnabled ? Sentry.withProfiler(App) : App;
